import { createErrorClass } from '@canvas/error';
import { isOnline } from '@canvas/network';
import { retryWithBackoff } from '@canvas/retry';

export const DynamicImportError = createErrorClass('DynamicImportError');
export const DynamicImportRetryError = createErrorClass('DynamicImportRetryError');

/**
 * Receives a function that includes an import that will be evaluated at runtime.
 * Example: const someImport = () => dynamicImport(() => import('@canvas/some-dep'));
 * ⚠️ Note that if dynamicImport is *not* wrapped in some kind of function as in
 * the above example, it will be evaluated in global scope.
 * @param toImport Anonymous function including an import, e.g. () => import('@canvas/some-dep')
 * @returns Promise that will return the module being imported.
 */
export const dynamicImport = async <T>(toImport: () => Promise<T>) => {
  try {
    return await toImport();
  } catch (err: unknown) {
    return await retryDynamicModuleFetch<T>(toImport, err);
  }
};

/**
 * When an dynamic import fails, we have to figure out the asset path to retry.
 * This method gets the asset path for us to retry.
 * This technique is taken from https://github.com/fatso83/retry-dynamic-import/tree/main
 */
const uriOrRelativePathRegex = /"((\w+:(\/?\/?))?[^\s]+)"/;
function parseModulePathFromImporterBody(importer: () => any): string | null {
  const fnString = importer.toString();
  const match = fnString.match(uriOrRelativePathRegex);
  if (!match) {
    return null;
  }
  return match.filter((e) => e)[1];
}

async function retryDynamicModuleFetch<T>(
  importer: () => Promise<any>,
  originalError: unknown,
): Promise<T> {
  const path = parseModulePathFromImporterBody(importer);
  if (path) {
    try {
      return await retryWithBackoff({
        callback: async () => {
          const cacheBustedPath = `${path}?t=${+new Date()}`;
          /* oxlint-disable-next-line core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
          return await import(/* @vite-ignore */ cacheBustedPath);
        },
        maxRetries: 3,
      });
    } catch (e) {
      const isOffline = !(await isOnline());
      if (e instanceof Error) {
        throw new DynamicImportRetryError(
          `Failed to import module after retrying - ${e.message}, ${e.stack}`,
          { ignoreUfoReporting: isOffline },
        );
      }
      throw new DynamicImportRetryError(`Failed to import module after retrying`, {
        ignoreUfoReporting: isOffline,
      });
    }
  }

  const isOffline = !(await isOnline());
  if (originalError instanceof Error) {
    throw new DynamicImportError(
      `Failed to import module - failed to obtain path for retry - ${originalError.message}, ${originalError.stack}`,
      { ignoreUfoReporting: isOffline },
    );
  }
  throw new DynamicImportError(
    `Failed to import module -  failed to obtain path for retry`,
    { ignoreUfoReporting: isOffline },
  );
}
